import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import DisclaimerFooter from '../components/DisclaimerFooter';
import SubmitFaqPanel from '../components/SubmitFaqPanel';
import { useAuth } from '../contexts/AuthContext';
import { faqSummary, faqData } from '../data/faqPlatformFaqData';
// Styles are shared with the CHCA FAQ page so all three FAQ pages stay visually
// identical. FaqPlatformFaq.css is imported second and holds only this page's
// additions (the Overview's template download block).
import './ChcaFaq.css';
import './FaqPlatformFaq.css';

const BASE_PATH = '/faq-platform-faq';

// Overview sections that can be linked to directly, mapped to the tab they live
// on. The hash handler checks these before question ids, so a link to a section
// never trips the "question has been removed" notice.
const SECTION_ANCHORS = faqSummary.instructions?.anchor
  ? { [faqSummary.instructions.anchor]: 'info' }
  : {};

function FaqPlatformFaq() {
  // The page itself is public, but submitting is not: the Submit tab appears
  // only for accounts holding the '/faq-platform-faq' permission (admins
  // included). The upload API enforces the same row, so this only decides
  // whether the tab is worth showing — it is not the access control.
  const { loading: authLoading, hasPermission } = useAuth();
  const canSubmit = !authLoading && hasPermission('/faq-platform-faq');

  const location = useLocation();
  const navigate = useNavigate();

  // Parse the active tab from the URL path.
  const getTabFromPath = useCallback(() => {
    const path = location.pathname.replace(new RegExp(`^${BASE_PATH}/?`), '');
    const parts = path.split('/').filter(Boolean);
    if (parts[0] === 'faq') return 'faq';
    if (parts[0] === 'submit') return 'submit';
    return 'info';
  }, [location.pathname]);

  const [activeTab, setActiveTabState] = useState(getTabFromPath());
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [expandAll, setExpandAll] = useState(false);
  const [invalidLink, setInvalidLink] = useState(false);
  // Once the Submit tab has been opened its panel stays mounted (hidden when
  // another tab is active). The instructions for filling in the template live
  // on the Overview tab, so a petitioner will flip between tabs part-way
  // through the form; unmounting would throw away the file they had chosen and
  // anything they had typed.
  const [submitOpened, setSubmitOpened] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const invalidLinkRef = useRef(null);

  // Keep state in step with back/forward navigation.
  useEffect(() => {
    setActiveTabState(getTabFromPath());
  }, [location.pathname, getTabFromPath]);

  const setActiveTab = useCallback((tab) => {
    setActiveTabState(tab);
    if (tab === 'info') navigate(`${BASE_PATH}/overview`, { replace: false });
    else if (tab === 'faq') navigate(`${BASE_PATH}/faq`, { replace: false });
    else if (tab === 'submit') navigate(`${BASE_PATH}/submit`, { replace: false });
  }, [navigate]);

  useEffect(() => {
    document.title = 'FAQ Platform';
  }, []);

  useEffect(() => {
    if (activeTab === 'submit' && canSubmit) setSubmitOpened(true);
  }, [activeTab, canSubmit]);

  // Hash-based deep linking: to an Overview section, or to a specific question.
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (!hash) return;

    // A link to an Overview section. Get onto that tab first — this effect runs
    // again once the path has changed, and scrolls then, when the panel exists —
    // then bring the section heading to the top and move focus to it so a
    // screen-reader user lands where a sighted reader does.
    const sectionTab = SECTION_ANCHORS[hash];
    if (sectionTab) {
      if (getTabFromPath() !== sectionTab) {
        navigate(`${BASE_PATH}/overview` + location.hash, { replace: true });
        return;
      }
      setActiveTabState(sectionTab);
      const timer = setTimeout(() => {
        const heading = document.getElementById(hash);
        if (heading) {
          // Focus first (without scrolling), then scroll, so nothing runs after
          // the scroll starts that could interrupt it. Honour a reduced-motion
          // preference: jump rather than animate (WCAG 2.3.3).
          const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
          heading.focus({ preventScroll: true });
          heading.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        }
      }, 100);
      return () => clearTimeout(timer);
    }

    setActiveTabState('faq');
    navigate(`${BASE_PATH}/faq` + location.hash, { replace: true });

    const validIds = faqData.map(item => item.id);
    if (validIds.includes(hash)) {
      setExpandedItems(prev => new Set(prev).add(hash));
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      setInvalidLink(true);
      setTimeout(() => {
        if (invalidLinkRef.current) {
          invalidLinkRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location.hash, navigate, getTabFromPath]);

  // Links inside FAQ answers are authored HTML, so React Router never sees them
  // and a click would reload the whole page. Route links to this page through
  // the router instead — no reload, and the section-anchor handling above can
  // scroll to its target. Modified clicks (open in new tab, etc.), downloads and
  // links elsewhere are left to the browser.
  const handleAnswerLinkClick = (e) => {
    const link = e.target.closest('a[href]');
    if (!link) return;
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
    const url = new URL(link.href, window.location.origin);
    if (url.origin !== window.location.origin || !url.pathname.startsWith(BASE_PATH)) return;
    e.preventDefault();
    navigate(url.pathname + url.search + url.hash);
  };

  const toggleItem = (id) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllItems = () => {
    if (expandAll) {
      setExpandedItems(new Set());
    } else {
      setExpandedItems(new Set(faqData.map(item => item.id)));
    }
    setExpandAll(!expandAll);
  };

  // Renders **bold**, bullet lists (lines starting with •) and numbered lists.
  const formatText = (text) => {
    let formatted = text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    const paragraphs = formatted.split('\n\n');

    const grouped = [];
    let i = 0;
    while (i < paragraphs.length) {
      const p = paragraphs[i];
      const lines = p.split('\n');
      const isNumbered = lines.some(l => /^\d+\.\s/.test(l.trim()));
      if (isNumbered) {
        const introLines = [];
        const numberedItems = [];
        const firstNumIdx = lines.findIndex(l => /^\d+\.\s/.test(l.trim()));
        if (firstNumIdx > 0) {
          introLines.push(...lines.slice(0, firstNumIdx).filter(l => l.trim()));
        }
        const numLines = lines.slice(firstNumIdx).filter(l => l.trim());
        numberedItems.push(numLines.join(' '));
        i++;
        while (i < paragraphs.length) {
          const nextLines = paragraphs[i].split('\n').filter(l => l.trim());
          if (nextLines.length > 0 && /^\d+\.\s/.test(nextLines[0].trim())) {
            numberedItems.push(nextLines.join(' '));
            i++;
          } else {
            break;
          }
        }
        grouped.push({ type: 'ol', introLines, numberedItems });
      } else if (p.includes('•')) {
        grouped.push({ type: 'ul', paragraph: p });
        i++;
      } else {
        grouped.push({ type: 'p', paragraph: p });
        i++;
      }
    }

    return grouped.map((item, idx) => {
      if (item.type === 'ul') {
        const lines = item.paragraph.split('\n');
        const firstBulletIndex = lines.findIndex(l => l.trim().startsWith('•'));
        const introLines = lines.slice(0, firstBulletIndex).filter(l => l.trim());
        const bullets = lines.slice(firstBulletIndex).filter(l => l.trim().startsWith('•'));
        return (
          <div key={idx} className="answer-section">
            {introLines.length > 0 && (
              <p dangerouslySetInnerHTML={{ __html: introLines.join('<br/>') }} />
            )}
            <ul>
              {bullets.map((bullet, j) => (
                <li key={j} dangerouslySetInnerHTML={{ __html: bullet.replace(/^•\s*/, '').trim() }} />
              ))}
            </ul>
          </div>
        );
      }
      if (item.type === 'ol') {
        return (
          <div key={idx} className="answer-section">
            {item.introLines.length > 0 && (
              <p dangerouslySetInnerHTML={{ __html: item.introLines.join('<br/>') }} />
            )}
            <ol>
              {item.numberedItems.map((ni, j) => (
                <li key={j} dangerouslySetInnerHTML={{ __html: ni.replace(/^\d+\.\s*/, '').trim() }} />
              ))}
            </ol>
          </div>
        );
      }
      return <p key={idx} dangerouslySetInnerHTML={{ __html: item.paragraph.replace(/\n/g, '<br/>') }} />;
    });
  };

  const formatAnswer = formatText;

  const copyLink = (id) => {
    const url = `${window.location.origin}${window.location.pathname}#${id}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  return (
    <div className="chca-faq-page">
      <a href="#main-content" className="chca-skip-link">Skip to main content</a>
      <Header
        title="FAQ Platform"
      >
        <div className="chca-header-tabs">
          <button
            className={`chca-header-tab ${activeTab === 'info' ? 'active' : ''}`}
            onClick={() => setActiveTab('info')}
            aria-pressed={activeTab === 'info'}
          >
            Overview
          </button>
          <button
            className={`chca-header-tab ${activeTab === 'faq' ? 'active' : ''}`}
            onClick={() => setActiveTab('faq')}
            aria-pressed={activeTab === 'faq'}
          >
            FAQ
          </button>
          {canSubmit && (
            <button
              className={`chca-header-tab ${activeTab === 'submit' ? 'active' : ''}`}
              onClick={() => setActiveTab('submit')}
              aria-pressed={activeTab === 'submit'}
            >
              Submit
            </button>
          )}
        </div>
      </Header>

      <main id="main-content" tabIndex={-1} className="faq-content">

        {/* ===== OVERVIEW PANEL ===== */}
        {activeTab === 'info' && (
          <div className="faq-panel">
            <section className="summary-section">
              <div className="version-badge">{faqSummary.version} - {faqSummary.date}</div>
              <div className="summary-text">
                {formatText(faqSummary.intro)}
              </div>

              {faqSummary.instructions && (
                <div className="template-instructions">
                  {/* tabIndex -1: not a tab stop, but focusable so a link to
                      this section can move focus here. */}
                  <h2
                    id={faqSummary.instructions.anchor}
                    className="template-instructions-heading template-section-anchor"
                    tabIndex={-1}
                  >
                    {faqSummary.instructions.heading}
                  </h2>
                  {faqSummary.instructions.lead && (
                    <p className="template-instructions-lead">
                      {faqSummary.instructions.lead}
                    </p>
                  )}
                  {/* Main steps are a numbered list whose titles are real <h3>s,
                      so the steps are navigable by heading; sub-steps are a
                      nested list, lettered to tell them apart. Titles and bodies
                      carry authored HTML links (the template download, sign-up). */}
                  <ol className="template-steps">
                    {faqSummary.instructions.steps.map((step, i) => (
                      <li key={i} className="template-step">
                        <h3
                          className="template-step-title"
                          dangerouslySetInnerHTML={{ __html: step.title }}
                        />
                        {step.body && (
                          <p
                            className="template-step-body"
                            dangerouslySetInnerHTML={{ __html: step.body }}
                          />
                        )}
                        {step.substeps && step.substeps.length > 0 && (
                          <ol className="template-substeps">
                            {step.substeps.map((sub, j) => (
                              <li key={j}>{sub}</li>
                            ))}
                          </ol>
                        )}
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {faqSummary.template && (
                <div className="template-download">
                  <a
                    className="template-download-btn"
                    href={faqSummary.template.file}
                    download={faqSummary.template.downloadName}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    <span>{faqSummary.template.label}</span>
                  </a>
                  {faqSummary.template.note && (
                    <p className="template-download-note">{faqSummary.template.note}</p>
                  )}
                </div>
              )}
            </section>
          </div>
        )}

        {/* ===== FAQ PANEL ===== */}
        {activeTab === 'faq' && (
          <div className="faq-panel">
            {invalidLink && (
              <div className="invalid-link-notice" role="alert" ref={invalidLinkRef}>
                The link you followed refers to a question that has been removed from this FAQ.
              </div>
            )}

            <section className="faq-section">
              <div className="faq-header">
                <h2>Questions &amp; Answers</h2>
                <button className="expand-toggle" onClick={toggleAllItems} aria-pressed={expandAll}>
                  {expandAll ? 'Collapse All' : 'Expand All'}
                </button>
              </div>

              <div className="faq-list" onClick={handleAnswerLinkClick}>
                {faqData.map((item, idx) => {
                  // v7 groups questions into titled sections; render the section
                  // heading (h3) before the first question of each section.
                  const showSection = item.section && (idx === 0 || faqData[idx - 1].section !== item.section);
                  return (
                    <React.Fragment key={item.id}>
                      {showSection && (
                        <h3 className="faq-section-heading">{item.section}</h3>
                      )}
                      <div
                        id={item.id}
                        className={`faq-item ${expandedItems.has(item.id) ? 'expanded' : ''}`}
                      >
                        <h4 className="faq-question-heading"><button
                          className="faq-question"
                          onClick={() => toggleItem(item.id)}
                          aria-expanded={expandedItems.has(item.id)}
                        >
                          <span className="question-text">{item.question}</span>
                          <span className="expand-icon" aria-hidden="true">{expandedItems.has(item.id) ? '−' : '+'}</span>
                        </button></h4>
                        {expandedItems.has(item.id) && (
                          <div className="faq-answer">
                            {formatAnswer(item.answer)}
                            {item.image && (
                              <div className="faq-image">
                                <img src={item.image.src} alt={item.image.alt} />
                              </div>
                            )}
                            <div className="copy-link-wrapper">
                              <button
                                className="copy-link-button"
                                onClick={(e) => { e.stopPropagation(); copyLink(item.id); }}
                                aria-label={copiedId === item.id ? 'Link copied to clipboard' : 'Copy link to this question'}
                              >
                                {copiedId === item.id ? 'Copied!' : '🔗'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </section>
          </div>
        )}

        {/* ===== SUBMIT PANEL ===== */}
        {submitOpened && canSubmit && (
          <div className="faq-panel" hidden={activeTab !== 'submit'}>
            <SubmitFaqPanel />
          </div>
        )}

        {/* Someone following a /submit link without access: say why rather than
            showing an upload form that the API would reject. */}
        {activeTab === 'submit' && !canSubmit && !authLoading && (
          <div className="faq-panel">
            <section className="summary-section">
              <h2 className="template-instructions-heading">Submitting content</h2>
              <p>
                Uploading a FAQ document is limited to petitioners whose FAQ is
                being hosted here. If that is you and you cannot see the Submit
                tab, contact Michael Rubenstein to have access turned on for
                your account.
              </p>
              <p>
                Everything you need to prepare a submission — the template and
                the instructions for filling it in — is on the{' '}
                <button
                  type="button"
                  className="faq-inline-link"
                  onClick={() => setActiveTab('info')}
                >
                  Overview tab
                </button>.
              </p>
            </section>
          </div>
        )}

        {faqSummary.disclaimer && (
          <DisclaimerFooter>
            <div dangerouslySetInnerHTML={{ __html: faqSummary.disclaimer }} />
          </DisclaimerFooter>
        )}
      </main>
    </div>
  );
}

export default FaqPlatformFaq;
